// FormBlock — craftjs Build-Menu block (NEW, Premium). Fable Hail Mary Wave 5
// (Lane-N, BLOCK-FORMS). Deck: concepts/builder-blocks/05-form-block +
// 07/08-form-responses. Renders the live form (title + typed fields + submit
// button); the Settings slot bridges node props to the design-system FormEditor.
//
// PREMIUM gate: SERVER-enforced & fail-closed (BE: response storage + the owner
// Responses view require the wheel owner's tier >= Premium). Authoring surface
// unflagged. The render does not POST inside the builder — ViewWheel submission
// wiring lands in remaining[].

import { useEditor, useNode } from "@craftjs/core";
import React from "react";
import { BlockIndicator } from "../../Shared/BlockIndicator";
import FormEditor, { FORM_DEFAULT, DEFAULT_PLACEHOLDER_BY_TYPE } from "../../BlockEditorPane/editors/FormEditor";
import { FORM_BLOCK_SUBMIT_ENABLED } from "../../../../constants/featureFlags";
import { usePaletteColor } from "../../../../hooks/usePaletteColor";
import { autoTextColor } from "../../../../utils/contrast";
import { useSegmentContext } from "../../../../pages/ViewWheel/SegmentContext";
import { spacing } from "../../../../theme/tokens";
import { formFieldInk } from "./formFieldInk";
import CalendarIcon from "./CalendarIcon";
import FormLiveView from "./FormLiveView";

const INK = "#123051";
const REQUIRED_RED = "#D92D20";
const BORDER = "#E4E7EC";
const FONT = "DM Sans, sans-serif";

// ISP-9999 (item 9): the static preview's placeholder text follows the SAME
// independent-of-label contract FormLiveView uses — a stored `field.placeholder`
// wins, else the type-level default, and it never re-derives from the label.
const previewPlaceholder = (field) =>
  field.placeholder || DEFAULT_PLACEHOLDER_BY_TYPE[field.type] || "Your answer";

// ISP-9999 (item 1): `labelInk`/`requiredInk` are resolved ONCE per render by
// the caller (against the segment's background) and threaded down, rather than
// re-resolved per field — same auto-contrast law as the poll question / blocks
// auto-contrast rule, via formFieldInk (see that file for the "image fills are
// not statically samplable" scope note).
const FieldPreview = ({ field, labelInk, requiredInk }) => {
  const label = (
    <label style={{ fontFamily: FONT, fontSize: 12, fontWeight: 600, color: labelInk, display: "block", marginBottom: 4 }}>
      {field.label || "Field"}
      {field.required && <span style={{ color: requiredInk, marginLeft: 3 }}>*</span>}
    </label>
  );
  const boxStyle = {
    width: "100%",
    minHeight: 40,
    border: `1px solid ${BORDER}`,
    borderRadius: 8,
    padding: "0 12px",
    fontFamily: FONT,
    fontSize: 14,
    color: "#98A2B3",
    background: "#fff",
    display: "flex",
    alignItems: "center",
  };

  if (field.type === "checkbox") {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ width: 18, height: 18, border: `1.5px solid ${BORDER}`, borderRadius: 4, flex: "none" }} />
        <span style={{ fontFamily: FONT, fontSize: 14, color: labelInk }}>
          {field.label || "Field"}
          {field.required && <span style={{ color: requiredInk, marginLeft: 3 }}>*</span>}
        </span>
      </div>
    );
  }
  if (field.type === "dropdown") {
    return (
      <div>
        {label}
        <div style={{ ...boxStyle, justifyContent: "space-between" }}>
          <span>Choose…</span>
          <span aria-hidden="true">▾</span>
        </div>
      </div>
    );
  }
  if (field.type === "date") {
    return (
      <div>
        {label}
        <div style={{ ...boxStyle, justifyContent: "space-between" }}>
          <span>Pick a date</span>
          {/* ISP-9999 (item 5): the SAME icon FormLiveView's date field uses —
              no more "📅" emoji drifting from the live view's real icon. */}
          <CalendarIcon size={16} color="#98A2B3" />
        </div>
      </div>
    );
  }
  // ISP-9999 (item 2): "Long answer" — a taller, multi-line-looking box.
  if (field.type === "longtext") {
    return (
      <div>
        {label}
        <div style={{ ...boxStyle, minHeight: 88, alignItems: "flex-start", paddingTop: 10 }}>
          {previewPlaceholder(field)}
        </div>
      </div>
    );
  }
  return (
    <div>
      {label}
      <div style={boxStyle}>{previewPlaceholder(field)}</div>
    </div>
  );
};

export const Form = ({ title, fields, submitLabel, submitColor, margin }) => {
  const {
    connectors: { connect, drag },
    id,
  } = useNode();
  // Craft editor is disabled (enabled === false) on the live wheel (ViewWheel);
  // enabled === true in the builder. Reading it changes no DOM.
  const { enabled } = useEditor((state) => ({ enabled: state.options.enabled }));
  const { getPaletteColor } = usePaletteColor();
  // ISP-9999 (item 1): the segment this block renders inside — used only to
  // pick a legible label/required-marker ink (formFieldInk); see that file for
  // the "image wallpapers can't be sampled statically" scope note. Available in
  // BOTH the builder canvas and ViewWheel (EmptyContainer already relies on the
  // same context in the builder canvas).
  const segment = useSegmentContext();

  const list = fields?.length ? fields : FORM_DEFAULT.fields;
  // ISP-9999: resolve the symbolic segment-color default (or a literal user
  // override) to a real hex ONCE, here, before it reaches either render path.
  const resolvedAccent = getPaletteColor(submitColor || FORM_DEFAULT.submitColor);
  const labelInk = formFieldInk(segment?.settings, INK);
  const requiredInk = formFieldInk(segment?.settings, REQUIRED_RED);
  const marginStyle = margin
    ? `${margin.top || 0}px ${margin.right || 0}px ${margin.bottom || 0}px ${margin.left || 0}px`
    : undefined;

  // FLAG-GATED live submission: only on the live wheel and only when ON. With the
  // flag OFF this branch is dead, so the render below is byte-identical to today
  // (the same static authoring preview in both the builder and ViewWheel).
  if (FORM_BLOCK_SUBMIT_ENABLED && enabled === false) {
    return (
      <div ref={(ref) => connect(drag(ref))}>
        <BlockIndicator>
          <div style={{ padding: marginStyle }}>
            <FormLiveView
              title={title}
              fields={list}
              submitLabel={submitLabel}
              submitColor={resolvedAccent}
              blockId={id}
            />
          </div>
        </BlockIndicator>
      </div>
    );
  }

  return (
    <div ref={(ref) => connect(drag(ref))}>
      <BlockIndicator>
        <div style={{ padding: marginStyle }}>
          {/* ISP-9999 (item 7): NO uniform `gap` here anymore — the title->fields
              gap stays 14px (unchanged), and the Send button gets its OWN
              margin-top below, set to the standard element-to-button token
              instead of inheriting the generic content rhythm. */}
          <div style={{ display: "flex", flexDirection: "column", padding: 4 }}>
            <div style={{ fontFamily: FONT, fontSize: 16, fontWeight: 700, color: title ? labelInk : "#98A2B3" }}>
              {title || "Your form title…"}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 14 }}>
              {list.map((f) => (
                <FieldPreview key={f.id} field={f} labelInk={labelInk} requiredInk={requiredInk} />
              ))}
            </div>
            <button
              type="button"
              style={{
                // ISP-9999 (item 7): right-justified, standard gap from the last
                // field — spacing.xl (24px), the same element-to-button token
                // MediaLibrary's Footer/UploadCta use, not an invented value.
                alignSelf: "flex-end",
                marginTop: spacing.xl,
                padding: "10px 24px",
                borderRadius: 10,
                border: "none",
                background: resolvedAccent,
                color: autoTextColor(resolvedAccent, { fallback: "#fff" }),
                fontFamily: FONT,
                fontSize: 14,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {submitLabel || "Send"}
            </button>
          </div>
        </div>
      </BlockIndicator>
    </div>
  );
};

const FormSettings = () => {
  const {
    actions: { setProp },
    title,
    fields,
    submitLabel,
    submitColor,
    emailEachResponse,
    margin,
  } = useNode((node) => ({
    title: node.data.props.title,
    fields: node.data.props.fields,
    submitLabel: node.data.props.submitLabel,
    submitColor: node.data.props.submitColor,
    emailEachResponse: node.data.props.emailEachResponse,
    margin: node.data.props.margin,
  }));
  // ISP-9999: hand FormEditor the RAW submitColor (sentinel-or-literal) —
  // never a pre-resolved hex. ColorPopup resolves "suggested_color_0" itself
  // (its own getPaletteColor(background) call) purely for display. Resolving
  // it HERE would round-trip through FormEditor's onChange on the NEXT
  // unrelated edit (a field label, the submit label…) and freeze the
  // sentinel into a literal hex, silently breaking live-tracking for a block
  // the user never touched the color of.
  const value = {
    title: title ?? FORM_DEFAULT.title,
    fields: fields ?? FORM_DEFAULT.fields,
    submitLabel: submitLabel ?? FORM_DEFAULT.submitLabel,
    submitColor: submitColor ?? FORM_DEFAULT.submitColor,
    emailEachResponse: emailEachResponse ?? FORM_DEFAULT.emailEachResponse,
    margin: margin ?? FORM_DEFAULT.margin,
  };

  return (
    <FormEditor
      value={value}
      onChange={(next) =>
        setProp((props) => {
          props.title = next.title;
          props.fields = next.fields;
          props.submitLabel = next.submitLabel;
          props.submitColor = next.submitColor;
          props.emailEachResponse = next.emailEachResponse;
          props.margin = next.margin;
        })
      }
    />
  );
};

export const FormDefaultProps = { ...FORM_DEFAULT };

Form.craft = {
  props: FormDefaultProps,
  related: {
    settings: FormSettings,
  },
  displayName: "FormWidget",
};

export default Form;
